import {useDispatch} from 'react-redux';
import {useCallback, useEffect, useRef} from 'react';

import {useDeployTask} from './useDeployTask';
import {addListener} from '../../../store/statusChecker';
import {TaskInfo} from '../../../types/metadata/TaskInfo';
import {toast} from '../../../base-components/StudioToast';
import {TestInitiateRequest} from '../../../api/test/TestInitiate';
import {Deployment} from '../../../types/deployment/DeploymentResponse';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {StreamingDevice} from '../../../types/deployment/StreamingDeviceResponse';
import {AnalyticsParametersAnswer} from '../../../types/prompts/AnalyticsParameters';
import {useDeploy, TASK_STATUS, resetAllTasks, setDeployTask} from '../useDeploy';
import {VideoStreamSubject} from '../HlsVideoStream';
import Util from '../../../util';

export const useHlsStreamingTask = (projectId: string) => {
  const reduxDispatch = useDispatch();
  const [{deployTask}, dispatch] = useDeploy();

  const {startDeploy, terminateDeploy} = useDeployTask(projectId);

  const cleanUpStream = useCallback(() => {
    if (deployTask.status === TASK_STATUS.IN_PROGRESS) {
      terminateDeploy();
    }
  }, [deployTask.status, terminateDeploy]);

  const cleanUpStreamRef = useRef(cleanUpStream);

  useEffect(() => {
    cleanUpStreamRef.current = cleanUpStream;
  }, [cleanUpStream]);

  useEffect(function stopStreamIfUnmounted() {
    return () => {
      if (cleanUpStreamRef.current) {
        cleanUpStreamRef.current();
      }
    };
  }, []);

  useEffect(
    function stopStreamIfWindowClosed() {
      window.addEventListener('beforeunload', cleanUpStream);
      return () => {
        window.removeEventListener('beforeunload', cleanUpStream);
      };
    },
    [cleanUpStream]
  );

  const monitorStatus = useCallback(
    (deploymentId, onMonitoringResponse) => {
      let wereParametersFound = false;
      reduxDispatch(
        addListener('GSP_DEPLOY', (status: TaskInfo) => {
          if (status.deploymentId !== deploymentId) {
            return;
          }
          dispatch(setDeployTask({monitoringResponse: status, deploymentId}));
          if (status.warnings.length > 0 && status.warnings[0]['detailMessage']) {
            toast.warn(`Warning: ${status.warnings[0]['detailMessage']}`);
          }
          if (status.failed) {
            if (status.errors.length > 0 && status.errors[0]['detailMessage']) {
              toast.error(`Inference error: ${status.errors[0]['detailMessage']}`);
            } else {
              toast.error(`Inference error. Please try again.`);
            }
            terminateDeploy(deploymentId);
          }
          if (Util.isRealNumberEqual(status.percentCompleted, 100) && status.finished) {
            dispatch(setDeployTask({status: TASK_STATUS.COMPLETE, deploymentId}));
          }
          if (!wereParametersFound && status.gspBoxOutputUrl) {
            wereParametersFound = true;
            onMonitoringResponse?.(status);
          }
        })
      );
    },
    [dispatch, reduxDispatch, terminateDeploy]
  );

  const runStreamingDeploy = async (
    config: TestInitiateRequest,
    onMonitoringResponse?: (monitoringResponse: TaskInfo) => void,
    onStartDeploy?: (deployment: Deployment) => void,
    onError?: () => void
  ) => {
    try {
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS}));
      const deploymentId = await startDeploy(config, onStartDeploy);
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS, deploymentId}));
      monitorStatus(deploymentId, onMonitoringResponse);
    } catch (e) {
      if (onError) {
        onError();
      }
      dispatch(resetAllTasks());
    }
  };

  const runTestInference = ({
    projectId,
    applicationId,
    spaId,
    schedulerId,
    name,
    description,
    inferenceSource,
    streamingDevices,
    videoFile,
    ipCamera,
    streamSubject,
    environment,
    analyticsParameters,
    onStartDeploy,
  }: {
    projectId: string;
    applicationId: string;
    spaId?: string;
    schedulerId?: string;
    name: string;
    description: string;
    inferenceSource?: Exclude<InputSource, 'IMAGES'>;
    streamingDevices: StreamingDevice[];
    videoFile: string;
    ipCamera: string;
    streamSubject: VideoStreamSubject;
    environment?: Record<string, string>;
    analyticsParameters?: AnalyticsParametersAnswer;
    onStartDeploy?: (deployment: Deployment) => void;
  }) => {
    if (inferenceSource !== 'WEB_CAMERA') {
      const url =
        inferenceSource === 'IP_CAMERA'
          ? streamingDevices.find(camera => camera.id === ipCamera)?.url
          : streamingDevices.find(file => file.id === videoFile)?.url;
      //TODO: move this logic to the server
      const app_input = inferenceSource === 'APP_INPUT';
      const regions = streamingDevices.find(camera => camera.id === ipCamera)?.regions;
      const lines = streamingDevices.find(camera => camera.id === ipCamera)?.lines;
      runStreamingDeploy(
        {
          externalVideoInferenceRequest: {
            name,
            description,
            projectId,
            applicationId,
            spaId,
            schedulerId,
            inputSourceType: inferenceSource,
            environment,
            analyticsParameters,
            ...(!app_input
              ? {
                  streamingUrl: url,
                  lines,
                  regions,
                }
              : {}),
          },
        },
        monitoringResponse => {
          streamSubject.raiseReadyToStartEvent(monitoringResponse);
        },
        deployment => {
          if (onStartDeploy) {
            onStartDeploy(deployment);
          }
        },
        () => streamSubject.raiseStreamFinishEvent()
      );
    }
  };

  return [runTestInference] as const;
};
